import { FloatingPortal } from "@floating-ui/react";
import { FloatingOverlay } from "@floating-ui/react";
import { useClientQuery } from "@graphql/clients/clients.g";
import { useCurrentUserQuery } from "@graphql/users/users.g";
import { useEffect, useMemo, useState } from "react";
import { Outlet, UIMatch, useLocation, useMatches, useNavigate, useParams } from "react-router";
import c from "classnames";

import { useMainLayoutProvider } from "@providers/MainLayoutProvider/useMainLayoutProvider";

import { MainHeader } from "@components/MainHeader/MainHeader";
import { MainNavigation } from "@components/MainNavigation/MainNavigation";
import { PortalHeader } from "@components/Headers";

import { PortalPageContext } from "@layouts/PortalPage/PortalPageContext";

import { useBreakpoint } from "@utils/hooks/useBreakpoint";
import { TidioUserData, useTidioScript } from "@utils/hooks/useTidioScript";

import classes from "./Portal.module.css";

export const Portal = () => {
  const { isSmallerThan } = useBreakpoint();
  const { isMobileNavigationOpen } = useMainLayoutProvider();
  const currentUserQuery = useCurrentUserQuery();
  const refetchCurrentUser = currentUserQuery.refetch;
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams();
  const matches = useMatches() as UIMatch<unknown, { fullBleed?: boolean }>[];
  const isFullBleed = matches.some((match) => match.handle?.fullBleed);
  
  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);
  const [subNavigation, setSubNavigation] = useState<React.ReactNode>(null);

  // Load client data if clientId is available
  const clientQuery = useClientQuery({ clientId }, { enabled: !!clientId });

  // Load Tidio chat script when user is authenticated
  const isUserAuthenticated = !!currentUserQuery.data?.currentUser;

  // Prepare user data for Tidio
  const tidioUserData: TidioUserData = useMemo(() => {
    const user = currentUserQuery.data?.currentUser;
    const client = clientQuery.data?.client;

    return {
      userId: user?.id,
      userName: user?.name,
      userEmail: user?.email,
      organizationName: client?.name,
    };
  }, [currentUserQuery.data?.currentUser, clientQuery.data?.client]);

  useTidioScript(isUserAuthenticated, tidioUserData);

  useEffect(() => {
    (async () => {
      const response = await refetchCurrentUser();
      const isLoggedIn = !!response.data?.currentUser;

      if (!isLoggedIn) {
        navigate("/login", { viewTransition: true, replace: true });
      }
    })();
  }, [
    currentUserQuery.data?.currentUser,
    navigate,
    refetchCurrentUser,
    location.pathname,
  ]);

  const mainNavigation = (
    <div className={classes.navigation}>
      <MainNavigation />
    </div>
  );

  return (
    <PortalPageContext.Provider value={{ setHeaderActions, setSubNavigation }}>
      <div className={classes.appContainer}>
        <MainHeader />
        <div className={classes.container}>
          {isSmallerThan.laptop ? (
            <FloatingPortal>
              {isMobileNavigationOpen && (
                <FloatingOverlay className={classes.overlay}>
                  {mainNavigation}
                </FloatingOverlay>
              )}
            </FloatingPortal>
          ) : (
            mainNavigation
          )}

          <div className={classes.mainContainer}>
            <PortalHeader>{headerActions}</PortalHeader>
            {subNavigation}
            <main className={c(classes.main, { [classes.fullBleed]: isFullBleed })}>
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    </PortalPageContext.Provider>
  );
};
